import {
  countSculptureEditionsByStatus,
  useDeleteSculptureEdition,
  useDuplicateSculptureEdition,
  useGetSculptureEditions,
} from "@/api/sculpture-edition/sculpture-edition";
import { CheckboxGroupMask } from "@/components/form-items/CheckboxGroupMask";
import DuplicateEntityDialog from "@/components/modal/DuplicateEntityDialog";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import AddSculptureEditionModal from "@/components/sculpture/AddSculptureEditionModal";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  sculptureEditionsAdvancedSearchConfig,
  sculptureEditionsColumns,
  sculptureEditionsColumnsVisibility,
} from "@/config/dashboard/sculpture-editions";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { SculptureEdition } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { isAxiosError } from "axios";
import { ACTION_VIEW_ICON } from "@/components/table/action-icons";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AdvancedSort } from "../table/AdvancedSort";
import {
  TableRowActionsMenu,
  type TableRowAction,
} from "@/components/table/TableRowActionsMenu";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import {
  ACTIONS_COLUMN_ID,
  createActionsColumnDef,
} from "@/components/table/table-actions-column";
import SculptureEditionPreviewSheet from "./SculptureEditionPreviewSheet";
import SculptureEditionSheetForm from "./SculptureEditionSheetForm";

// ─── Duplicate dialog ─────────────────────────────────────────────────────────

const duplicateSchema = z.object({
  sculptureEditionNumeroEnregistrement: z.string().min(1, "required"),
});

type DuplicateFormType = z.infer<typeof duplicateSchema>;

function DuplicateSculptureEditionDialog({
  open,
  onOpenChange,
  sourceId,
  onSuccess: onDuplicateSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceId: string | undefined;
  onSuccess?: (edition: { id: string }) => void;
}) {
  const { t } = useTranslation();
  const form = useForm<DuplicateFormType>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(duplicateSchema),
    defaultValues: { sculptureEditionNumeroEnregistrement: "" },
  });
  const { mutateAsync, isPending } = useDuplicateSculptureEdition();

  const handleSubmit = async (data: DuplicateFormType) => {
    if (!sourceId) return;
    try {
      const created = await mutateAsync({
        sourceId,
        sculptureEditionNumeroEnregistrement:
          data.sculptureEditionNumeroEnregistrement,
      });
      onDuplicateSuccess?.({ id: created.id });
      onOpenChange(false);
      form.reset();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("sculptureEditionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <DuplicateEntityDialog<DuplicateFormType>
      open={open}
      onOpenChange={onOpenChange}
      title={t("sculpture.form.duplicateSculptureEdition")}
      form={form}
      onSubmit={handleSubmit}
      isLoading={isPending}
    >
      <FormField
        control={form.control}
        name="sculptureEditionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("sculpture.form.sculptureEditionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </DuplicateEntityDialog>
  );
}

// ─── Table storage key ────────────────────────────────────────────────────────

const STORAGE_KEY = "sculpture-edition-table-preferences";

const defaultVisibleColumns = [
  ...sculptureEditionsColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const ensureActionsColumnLast = (order: string[]) => {
  const uniqueOrder = Array.from(new Set(order));
  const withoutActions = uniqueOrder.filter((id) => id !== ACTIONS_COLUMN_ID);
  return [...withoutActions, ACTIONS_COLUMN_ID];
};

// ─── Main component ───────────────────────────────────────────────────────────

interface SculptureEditionsTableProps {
  masterArtworkId: string;
  artworkTitle: string;
}

const SculptureEditionsTable = ({
  masterArtworkId,
  artworkTitle,
}: SculptureEditionsTableProps) => {
  const { t } = useTranslation();

  const {
    pagination,
    setPagination,
    countByStatus,
    currentCount,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<SculptureEdition>({
    resourceName: "sculptureEdition",
    statusField: "sculptureEditionPublicationStatus",
    resourceCounter: (where) =>
      countSculptureEditionsByStatus(masterArtworkId, where),
  });

  const isFirstChild = useMemo(
    () =>
      ((countByStatus?.published as number) ?? 0) +
        ((countByStatus?.draft as number) ?? 0) ===
      0,
    [countByStatus],
  );

  const [duplicateSourceId, setDuplicateSourceId] = useState<
    string | undefined
  >();

  const { mutateAsync: deleteSculptureEdition } = useDeleteSculptureEdition();

  const [selectedEditionId, setSelectedEditionId] = useState<
    string | undefined
  >();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [previewEditionId, setPreviewEditionId] = useState<
    string | undefined
  >();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const openEditSheet = useCallback((editionId: string) => {
    setPreviewEditionId(undefined);
    setIsPreviewOpen(false);
    setSelectedEditionId(editionId);
    setIsSheetOpen(true);
  }, []);

  const openPreview = useCallback((editionId: string) => {
    setIsSheetOpen(false);
    setSelectedEditionId(undefined);
    setPreviewEditionId(editionId);
    setIsPreviewOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteSculptureEdition(id);
        toast({
          title: t("successDeleteSculptureEdition"),
          variant: "success",
        });
      } catch (error) {
        toast({
          title: t("errorDeleteSculptureEdition"),
          variant: "destructive",
        });
        console.error(error);
      }
    },
    [deleteSculptureEdition, t],
  );

  const defaultOrder = useMemo(() => [{ createdAt: "desc" as const }], []);

  const orderWithDefault = useMemo(
    () =>
      fetchParams.order && fetchParams.order.length > 0
        ? fetchParams.order
        : defaultOrder,
    [fetchParams.order, defaultOrder],
  );

  const { data } = useGetSculptureEditions(masterArtworkId, {
    where: fetchParams.where,
    order: orderWithDefault,
    take: fetchParams.take,
    skip: fetchParams.skip,
  });

  const columns: ColumnDef<SculptureEdition, unknown>[] = useMemo(() => {
    const baseColumns = generateTable(
      sculptureEditionsColumns,
      "sculptureEdition",
      t,
    ) as ColumnDef<SculptureEdition, unknown>[];

    return [
      ...baseColumns,
      createActionsColumnDef<SculptureEdition>(({ row }) => {
        const edition = row.original;
        const actions: TableRowAction[] = [
          {
            id: "edit",
            label: t(ACTION_I18N_KEYS.edit),
            icon: Pencil,
            onSelect: () => openEditSheet(edition.id),
          },
          {
            id: "duplicate",
            label: t(ACTION_I18N_KEYS.duplicate),
            icon: Copy,
            onSelect: () => setDuplicateSourceId(edition.id),
          },
          {
            id: "view",
            label: t(ACTION_I18N_KEYS.view),
            icon: ACTION_VIEW_ICON,
            onSelect: () => openPreview(edition.id),
          },
          {
            id: "delete",
            label: t(ACTION_I18N_KEYS.delete),
            icon: Trash2,
            destructive: true,
            onSelect: () => handleDelete(edition.id),
          },
        ];

        return (
          <div
            className="flex justify-end"
            onClick={(event) => event.stopPropagation()}
          >
            <TableRowActionsMenu actions={actions} />
          </div>
        );
      }),
    ];
  }, [t, handleDelete, openPreview, openEditSheet]);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      try {
        const { order, columnOrder } = saved;
        const loadedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : defaultVisibleColumns;
        return ensureActionsColumnLast(loadedOrder);
      } catch {
        return ensureActionsColumnLast(defaultVisibleColumns);
      }
    }
    return ensureActionsColumnLast(defaultVisibleColumns);
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        try {
          const { visibility } = saved;
          return visibility || {};
        } catch {
          // fallthrough
        }
      }
      return {
        ...sculptureEditionsColumnsVisibility,
        [ACTIONS_COLUMN_ID]: true,
      };
    },
  );

  const handleRowClick = (edition: SculptureEdition) => {
    openEditSheet(edition.id);
  };

  return (
    <CheckboxGroupMask
      groupName="artworkSculptureEditionsTableGroup"
      label={t("sculpture.form.sculptureEditions")}
      className="w-full"
    >
    <div className="flex flex-col items-start gap-3 w-full">
      <div className="flex justify-end items-center w-full">
        <GlobalSearch
          config={sculptureEditionsAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>

      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus ?? { published: 0, draft: 0 }}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSort config={sculptureEditionsAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("addSculptureEdition")}
            Dialog={(props) => (
              <AddSculptureEditionModal
                {...props}
                masterArtworkId={masterArtworkId}
                isFirstChild={isFirstChild}
                onCreateEdition={({ id }) => openEditSheet(id)}
              />
            )}
          />
        </div>
      </div>

      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>

      <div className="w-full">
        <DataTable
          pagination={{
            pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
          }}
          onRowClick={(row) => handleRowClick(row.original)}
        />
      </div>

      <SculptureEditionPreviewSheet
        editionId={previewEditionId}
        open={isPreviewOpen}
        onOpenChange={(open) => {
          setIsPreviewOpen(open);
          if (!open) setPreviewEditionId(undefined);
        }}
      />

      <SculptureEditionSheetForm
        editionId={selectedEditionId ?? ""}
        artworkTitle={artworkTitle}
        open={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          if (!open) {
            setSelectedEditionId(undefined);
          }
        }}
        onDeleted={() => {
          // Refresh the table or remove the deleted edition
          setIsSheetOpen(false);
          setSelectedEditionId(undefined);
        }}
      />

      <DuplicateSculptureEditionDialog
        open={!!duplicateSourceId}
        onOpenChange={(open) => !open && setDuplicateSourceId(undefined)}
        sourceId={duplicateSourceId}
        onSuccess={({ id }) => openEditSheet(id)}
      />
    </div>
    </CheckboxGroupMask>
  );
};

export default SculptureEditionsTable;
